// src/frontend/src/components/Common/UpgradeBanner.tsx - Enhanced with more aggressive upgrade prompts
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../api/api';

interface UpgradeBannerProps {
  planType: string;
  creditsPercentageUsed: number;
  trialDaysLeft?: number;
  teamPilotDaysLeft?: number;
  onDismiss?: () => void;
  isTrialExpired?: boolean;
  showUpgradeButton?: boolean; // New prop for persistent upgrade access
}

interface BannerInfo {
  show: boolean;
  text?: string;
  cta?: string;
  link?: string;
  color?: string;
  icon?: string;
  isUrgent?: boolean;
  showDirectPayment?: boolean; // New: Show direct payment button
}

const UpgradeBanner: React.FC<UpgradeBannerProps> = ({
  planType,
  creditsPercentageUsed,
  trialDaysLeft,
  teamPilotDaysLeft,
  onDismiss,
  isTrialExpired = false,
  showUpgradeButton = true
}) => {
  const navigate = useNavigate();
  const [isCreatingCheckout, setIsCreatingCheckout] = React.useState(false);

  const createCheckoutSession = async (planType: string, billingPeriod: string) => {
    setIsCreatingCheckout(true);
    try {
      const response = await api.post('/payments/create-checkout-session', {
        planType,
        billingPeriod
      });
      
      if (response.data.data.url) {
        window.location.href = response.data.data.url;
      }
    } catch (error: any) {
      console.error('Checkout error:', error);
      alert('Failed to start upgrade. Please try again.');
    } finally {
      setIsCreatingCheckout(false);
    }
  };

  // Enhanced conditions for showing banners with more scenarios
  const getBannerInfo = (): BannerInfo => {
    // HIGHEST PRIORITY: Trial expired - must subscribe
    if (isTrialExpired || (planType === 'free-trial' && trialDaysLeft === 0)) {
      return {
        show: true,
        text: '🚨 Your free trial has expired! Subscribe now to continue coaching.',
        cta: 'Subscribe Now',
        link: '/pricing?trial_expired=true',
        color: 'red',
        icon: 'warning',
        isUrgent: true,
        showDirectPayment: true
      };
    }
    
    // Critical trial expiration (1 day left)
    if (planType === 'free-trial' && trialDaysLeft === 1) {
      return {
        show: true,
        text: '⚠️ Your free trial expires tomorrow! Subscribe now to keep your progress.',
        cta: 'Subscribe Now',
        link: '/pricing?from=expiration',
        color: 'red',
        icon: 'warning',
        isUrgent: true,
        showDirectPayment: true
      };
    }
    
    // Trial ending soon (2-5 days)
    if (planType === 'free-trial' && trialDaysLeft && trialDaysLeft <= 5) {
      return {
        show: true,
        text: `Your free trial ends in ${trialDaysLeft} days. Subscribe now to keep practicing!`,
        cta: 'Subscribe Before It\'s Too Late',
        link: '/pricing?from=expiration',
        color: 'yellow',
        icon: 'clock',
        isUrgent: true,
        showDirectPayment: true
      };
    }

    // NEW: Trial users with moderate usage (50%+ credits used)
    if (planType === 'free-trial' && creditsPercentageUsed >= 50 && trialDaysLeft && trialDaysLeft > 5) {
      return {
        show: true,
        text: `You're loving PM Coach! You've used ${Math.round(creditsPercentageUsed)}% of your trial sessions. Get unlimited access with a paid plan.`,
        cta: 'Get Unlimited Sessions',
        link: '/pricing?from=usage',
        color: 'blue',
        icon: 'sparkle',
        showDirectPayment: true
      };
    }

    // NEW: Early trial users who haven't used much (less than 20% after 7+ days)
    if (planType === 'free-trial' && creditsPercentageUsed < 20 && trialDaysLeft && trialDaysLeft <= 7) {
      return {
        show: true,
        text: `Don't let your trial go to waste! You still have ${Math.round(100 - creditsPercentageUsed)}% of your sessions unused. Try a few more scenarios!`,
        cta: 'Continue Practicing',
        link: '/coaching-sessions',
        color: 'purple',
        icon: 'info'
      };
    }
    
    // Team pilot expiring soon
    if (planType === 'team-pilot' && teamPilotDaysLeft && teamPilotDaysLeft <= 7) {
      const urgency = teamPilotDaysLeft <= 3 ? '⚠️ ' : '';
      return {
        show: true,
        text: `${urgency}Your Team Pilot expires in ${teamPilotDaysLeft} days. Contact us to continue.`,
        cta: 'Contact Sales',
        link: '/contact?plan=team-renewal',
        color: teamPilotDaysLeft <= 3 ? 'red' : 'yellow',
        icon: 'team',
        isUrgent: teamPilotDaysLeft <= 3
      };
    }
    
    // Credits running very low (90%+)
    if (creditsPercentageUsed >= 90) {
      return {
        show: true,
        text: `⚠️ You've used ${Math.round(creditsPercentageUsed)}% of your monthly simulations!`,
        cta: 'Get More Credits Now',
        link: '/pricing',
        color: 'red',
        icon: 'alert',
        isUrgent: true,
        showDirectPayment: true
      };
    }
    
    // Solo Core upgrade prompt (more aggressive)
    if (planType === 'solo-core' && creditsPercentageUsed >= 70) {
      return {
        show: true,
        text: 'You\'re practicing a lot! Upgrade to Pro for 3x more sessions + real-time coaching tips!',
        cta: 'Upgrade to Pro Now',
        link: '/pricing?upgrade=solo-pro',
        color: 'blue',
        icon: 'sparkle',
        showDirectPayment: true
      };
    }

    // NEW: Solo Core users who are mid-month and using well
    if (planType === 'solo-core' && creditsPercentageUsed >= 50 && creditsPercentageUsed < 70) {
      return {
        show: true,
        text: 'Great progress! You\'re using PM Coach actively. Pro gives you 3x more sessions + real-time coaching!',
        cta: 'See Pro Benefits',
        link: '/pricing?upgrade=solo-pro',
        color: 'green',
        icon: 'sparkle',
        showDirectPayment: true
      };
    }

    // NEW: Any trial user after 3 days, regardless of usage
    if (planType === 'free-trial' && trialDaysLeft && trialDaysLeft <= 11 && showUpgradeButton) {
      return {
        show: true,
        text: 'Enjoying PM Coach? Lock in your progress with a paid plan and get access to advanced features!',
        cta: 'See Upgrade Options',
        link: '/pricing?from=trial',
        color: 'blue',
        icon: 'info',
        showDirectPayment: true
      };
    }
    
    // General low credits warning
    if (creditsPercentageUsed >= 75) {
      return {
        show: true,
        text: `You've used ${Math.round(creditsPercentageUsed)}% of your monthly simulations.`,
        cta: 'View Upgrade Options',
        link: '/pricing',
        color: 'yellow',
        icon: 'info',
        showDirectPayment: planType !== 'team-pilot'
      };
    }
    
    return { show: false };
  };

  const bannerInfo = getBannerInfo();
  
  // Early return if banner shouldn't show or is missing required properties
  if (!bannerInfo.show || !bannerInfo.text || !bannerInfo.icon) {
    return null;
  }

  const colorClasses = {
    red: 'bg-red-50 border-red-200 text-red-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800'
  };

  const icons = {
    warning: (
      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    clock: (
      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
      </svg>
    ),
    alert: (
      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
      </svg>
    ),
    sparkle: (
      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
      </svg>
    ),
    team: (
      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
      </svg>
    ),
    info: (
      <svg className="w-5 h-5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
      </svg>
    )
  };

  return (
    <div className={`${colorClasses[bannerInfo.color as keyof typeof colorClasses]} border rounded-lg p-4 mb-6 shadow-sm ${
      bannerInfo.isUrgent ? 'animate-pulse' : ''
    }`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center flex-1">
          {icons[bannerInfo.icon as keyof typeof icons]}
          <div className="flex-1">
            <p className="text-sm font-medium">{bannerInfo.text}</p>
            
            {/* NEW: Additional context for urgent situations */}
            {bannerInfo.isUrgent && isTrialExpired && (
              <p className="text-xs mt-1 opacity-80">
                Your coaching history and progress will be preserved when you subscribe.
              </p>
            )}
          </div>
        </div>
        
        <div className="flex items-center ml-4 space-x-2">
          {/* Direct payment button for urgent scenarios */}
          {bannerInfo.showDirectPayment && (bannerInfo.isUrgent || creditsPercentageUsed >= 90) && (
            <button
              onClick={() => createCheckoutSession(planType === 'solo-core' ? 'solo_pro' : 'solo_core', 'monthly')}
              disabled={isCreatingCheckout}
              className={`px-4 py-2 text-sm font-semibold rounded-md transition-colors disabled:opacity-50 ${
                bannerInfo.color === 'red' ? 'bg-red-600 hover:bg-red-700 text-white' :
                bannerInfo.color === 'yellow' ? 'bg-yellow-600 hover:bg-yellow-700 text-white' :
                'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isCreatingCheckout ? 'Setting up...' : 
               planType === 'solo-core' ? 'Upgrade to Pro' : 'Subscribe Now'}
            </button>
          )}

          {/* Regular CTA link */}
          {bannerInfo.cta && bannerInfo.link && (
            <Link
              to={bannerInfo.link}
              className={`text-sm font-medium underline hover:no-underline whitespace-nowrap ${
                bannerInfo.color === 'red' ? 'text-red-700' :
                bannerInfo.color === 'yellow' ? 'text-yellow-700' :
                bannerInfo.color === 'green' ? 'text-green-700' :
                bannerInfo.color === 'purple' ? 'text-purple-700' :
                'text-blue-700'
              }`}
            >
              {bannerInfo.cta} →
            </Link>
          )}
          
          {/* Dismiss button */}
          {onDismiss && !bannerInfo.isUrgent && (
            <button
              onClick={onDismiss}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Dismiss"
            >
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* NEW: Pro comparison for Solo Core users */}
      {planType === 'solo-core' && bannerInfo.showDirectPayment && (
        <div className="mt-3 pt-3 border-t border-blue-200 bg-blue-25">
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <div className="font-medium text-blue-800">Current (Solo Core)</div>
              <div className="text-blue-700">20 sessions/month</div>
            </div>
            <div>
              <div className="font-medium text-blue-800">With Pro Upgrade</div>
              <div className="text-blue-700">60 sessions/month + real-time tips</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UpgradeBanner;